// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The review/sync toasts and the coverage notice the survey screens show,
 * moved out of `routes/KortlaegningPage.tsx` so the Bygning shell's case
 * store (`app/bygning/useCaseData.ts`) and the Ressourcer/Gennemgang modes
 * speak with the same words. Pure: no React, no DOM.
 */

import type { ResourceView, Sample, SurveySummary, SurveySyncReport, SurveyType } from '../api/types';
import { tabCounts } from './model';
import { pendingSampleList } from './samples';
import { VIEW_KEY, viewValue } from './view';
import { formatNumber } from './vocab';

/**
 * The toast after a refused approval: names the samples still awaiting an
 * answer, the same way the gate note does (`pendingSampleList`).
 */
export function blockedMessage(type: SurveyType, samples: Sample[]): string {
  const names = pendingSampleList(type, samples);
  return names
    ? `Kan ikke godkendes — afventer prøvesvar (${names})`
    : 'Kan ikke godkendes — afventer prøvesvar';
}

/** The toast after an approval, counting what is left in the queue. */
export function approvedMessage(name: string, types: SurveyType[]): string {
  return `✓ ${name} godkendt · ${tabCounts(types).queue} tilbage i køen`;
}

/** How many room names the coverage notice spells out before "… og N flere". */
export const COVERAGE_ROOMS_SHOWN = 5;

/** `names` joined, capped at `shown`: "A, B, C … og 451 flere". */
export function cappedList(names: string[], shown = COVERAGE_ROOMS_SHOWN): string {
  if (names.length <= shown) return names.join(', ');
  return `${names.slice(0, shown).join(', ')} … og ${formatNumber(names.length - shown)} flere`;
}

/**
 * The coverage notice's clauses after `Dækning:`, or `[]` when there is
 * nothing to warn about. A clause whose figure is absent is left out. The
 * rooms clause waits until the survey has types: before the first sync every
 * room lacks parts, which says nothing.
 */
export function coverageParts(summary: SurveySummary): string[] {
  const parts: string[] = [];
  if (summary.unlabeled_points) {
    parts.push(`${formatNumber(summary.unlabeled_points)} punkter uklassificeret`);
  }
  const rooms = summary.rooms_without_parts;
  if (rooms.length > 0 && summary.counts.all > 0) {
    parts.push(
      `${formatNumber(rooms.length)} rum uden registrerede bygningsdele (${cappedList(rooms)})`,
    );
  }
  return parts;
}

/** The toast after "Opret kortlægning fra instanser": what sync did, and why not more. */
export function syncMessage(report: SurveySyncReport): string {
  if (report.parts_orphaned > 0) {
    return `${report.parts_orphaned} del(e) peger på instanser der ikke findes længere`;
  }
  const parts = report.parts_created;
  if (parts > 0) {
    const noun = parts === 1 ? 'bygningsdel' : 'bygningsdele';
    const types = report.types_created;
    if (types === 0) return `${formatNumber(parts)} ${noun} tilføjet til eksisterende typer`;
    return `${formatNumber(parts)} ${noun} oprettet i ${formatNumber(types)} ${types === 1 ? 'ny type' : 'nye typer'}`;
  }
  if (report.instances_seen === 0) {
    return 'Ingen instanser at kortlægge — opret instanser først';
  }
  const dismissed = report.parts_dismissed ?? 0;
  if (dismissed > 0) {
    return `Ingen nye bygningsdele — ${formatNumber(report.instances_seen - dismissed)} instanser er kortlagt, ${formatNumber(dismissed)} slettede genoprettes ikke`;
  }
  return `Ingen nye bygningsdele — alle ${formatNumber(report.instances_seen)} instanser er allerede kortlagt`;
}

/** Toast after Afvis: the type is kept, in the Afvist tab (spec A3). */
export const REJECTED_MESSAGE = 'Afvist som fejldetektion — flyttet til Afvist';

/** Toast after a type is deleted, naming how many resources went with it. */
export function typeDeletedMessage(name: string, parts: number): string {
  if (parts === 0) return `»${name}« slettet`;
  return `»${name}« slettet med ${formatNumber(parts)} ${parts === 1 ? 'ressource' : 'ressourcer'}`;
}

/**
 * The cell write a "Brug som ressourcebillede" choice makes, or null without
 * a target part. A resource always has an image once one is chosen, so this
 * only ever changes `sys:view` to a different value — there is no write that
 * clears it. (Moved from main's `routes/KortlaegningPage.tsx`; the Bygning
 * evidence tile wires it in frontend-r22.1.)
 */
export function chooseViewWrite(
  target: string | null,
  view: ResourceView,
): { code: string; keyId: typeof VIEW_KEY; value: string } | null {
  if (target === null) return null;
  return { code: target, keyId: VIEW_KEY, value: viewValue(view) };
}
